import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Calendar, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DemoSchedulingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    preferred_date: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/demo-requests`, formData);
      setSubmitted(true);
      toast.success('Demo request submitted successfully!');
      
      // Reset form after 3 seconds and close
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          preferred_date: '',
          message: ''
        });
        setSubmitted(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting demo request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit demo request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Close Button */}
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-10 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </Button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold">Schedule a Demo</h2>
                  <p className="text-teal-50 mt-1">See MyMedMember in action</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-6">
                {/* Name and Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Dr. John Smith"
                      className="border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@clinic.com"
                      className="border-gray-300"
                    />
                  </div>
                </div>

                {/* Phone and Company */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      className="border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">
                      Clinic/Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Wellness Family Clinic"
                      className="border-gray-300"
                    />
                  </div>
                </div>

                {/* Preferred Date */}
                <div className="space-y-2">
                  <Label htmlFor="preferred_date">
                    Preferred Date & Time
                  </Label>
                  <Input
                    id="preferred_date"
                    type="text"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    placeholder="e.g., Next week, Tuesday 2 PM, etc."
                    className="border-gray-300"
                  />
                  <p className="text-sm text-gray-500">
                    <Clock className="h-3 w-3 inline mr-1" />
                    We'll contact you to confirm the exact time
                  </p>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">
                    Tell us about your needs
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    placeholder="What are you looking for in a membership management platform?"
                    className="border-gray-300"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white py-6 text-lg"
                  >
                    {loading ? 'Submitting...' : 'Schedule My Demo'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="px-8 py-6"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          </>
        ) : (
          /* Success State */
          <div className="p-12 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Demo Request Submitted!
              </h3>
              <p className="text-gray-600 text-lg">
                Thank you for your interest in MyMedMember!
              </p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-6">
              <p className="text-gray-700">
                Our team will contact you within <strong>24 hours</strong> to schedule your personalized demo.
              </p>
            </div>
            <p className="text-sm text-gray-500">
              Check your email for a confirmation message.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoSchedulingModal;
